import { NavLink } from 'react-router-dom'
import {
  Activity,
  Ban,
  CalendarDays,
  Contact,
  FileText,
  History,
  Home,
  Megaphone,
  MessagesSquare,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'

interface NavItem {
  to: string
  label: string
  icon: typeof Home
  adminOnly?: boolean
  /** Story 12.10 (Epic E12) — visible to admin AND manager (unlike `adminOnly`). */
  adminManagerOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: Home },
  { to: '/admin/sellers', label: 'Sellers', icon: Users },
  { to: '/admin/conversations', label: 'Conversations', icon: MessagesSquare },
  { to: '/admin/templates', label: 'Templates', icon: FileText },
  { to: '/admin/prompts', label: 'Prompts', icon: ScrollText },
  // Story 3.18 — PromptAssistant wizard (admin/manager only — the page itself
  // redirects other roles). Kept as a sibling instead of a nested item for
  // visual parity with the rest of the rail.
  { to: '/admin/prompts/assistant', label: 'Prompt Assistant', icon: Sparkles },
  { to: '/admin/campaigns', label: 'Campaigns', icon: Megaphone },
  // Story 3.9 — opt-out keyword registry
  { to: '/admin/opt-out-keywords', label: 'Opt-out Keywords', icon: Ban },
  // Story 3.17 — read-only audit log viewer (admin + manager)
  { to: '/admin/audit-log', label: 'Audit Log', icon: History },
  // Story 6.4 — HealthMonitor dashboard (auth required, no role gate)
  { to: '/admin/health', label: 'Health', icon: Activity },
  { to: '/admin/vendor-calendars', label: 'Calendários GHL', icon: CalendarDays },
  // Story 12.10 (Epic E12) — manager+admin acompanham a carteira de cada SDR.
  { to: '/admin/carteiras', label: 'Carteiras', icon: Contact, adminManagerOnly: true },
  // Users CRUD — admin-only (página /admin/users redireciona caso contrário)
  { to: '/admin/users', label: 'Usuários', icon: ShieldCheck, adminOnly: true },
  { to: '/settings', label: 'Settings', icon: Settings },
]

/**
 * Left navigation rail used by the AdminLayout.
 * Visual-only on small screens — replaced by Sheet trigger in Topbar.
 */
export function Sidebar(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const isAdminOrManager = role === 'admin' || role === 'manager'
  // 2026-07-21 (user request) — vendedor only gets Conversations in the nav
  // (mirrors the redirect guard in `AdminLayout`, which is the part that
  // actually enforces it — this just keeps the rail from listing links a
  // vendedor would immediately bounce off of).
  // Story 12.8 (Epic E12) — same treatment as vendedor above; sdr only reads/
  // acts on her own carteira via Conversations (Stories 12.4/12.5).
  const items =
    role === 'vendedor' || role === 'sdr'
      ? NAV_ITEMS.filter((item) => item.to === '/admin/conversations')
      : NAV_ITEMS.filter(
          (item) =>
            (!item.adminOnly || isAdmin) && (!item.adminManagerOnly || isAdminOrManager),
        )

  return (
    <aside
      aria-label="Navegação principal"
      className="hidden h-full w-60 shrink-0 border-r bg-muted/30 md:flex md:flex-col"
    >
      <div className="flex h-16 items-center border-b px-6">
        <span className="text-lg font-semibold">SDR Agend</span>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                )
              }
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          )
        })}
      </nav>
    </aside>
  )
}
